import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  channelScrapingService,
  ChannelScrapingServerPayload,
  ChannelScrapingTaskPayload,
  ChannelScrapingWorkerServerPayload,
} from '@/services/channelScrapingService';

export const channelScrapingKeys = {
  all: ['channelScraping'] as const,
  servers: (scrapingServerId?: string | null) => [...channelScrapingKeys.all, 'servers', scrapingServerId ?? 'default'] as const,
  scrapingServers: () => [...channelScrapingKeys.all, 'scrapingServers'] as const,
  tasks: (scrapingServerId?: string | null) => [...channelScrapingKeys.all, 'tasks', scrapingServerId ?? 'default'] as const,
  task: (id: string, scrapingServerId?: string | null) => [...channelScrapingKeys.all, 'task', id, scrapingServerId ?? 'default'] as const,
};

export const useChannelScrapingServers = (scrapingServerId?: string | null) =>
  useQuery({
    queryKey: channelScrapingKeys.servers(scrapingServerId),
    queryFn: () => channelScrapingService.getServers(scrapingServerId),
    select: (data) => data.servers,
  });

export const useChannelScrapingWorkerServers = () =>
  useQuery({
    queryKey: channelScrapingKeys.scrapingServers(),
    queryFn: channelScrapingService.getScrapingServers,
    select: (data) => data.servers,
  });

export const useChannelScrapingTasks = (scrapingServerId?: string | null) =>
  useQuery({
    queryKey: channelScrapingKeys.tasks(scrapingServerId),
    queryFn: () => channelScrapingService.getTasks(scrapingServerId),
    select: (data) => data.tasks,
  });

export const useChannelScrapingTask = (id: string | undefined, scrapingServerId?: string | null) =>
  useQuery({
    queryKey: channelScrapingKeys.task(id ?? 'unknown', scrapingServerId),
    queryFn: () => {
      if (!id) {
        throw new Error('Tarea no encontrada');
      }
      return channelScrapingService.getTask(id, scrapingServerId);
    },
    enabled: Boolean(id),
    select: (data) => data.task,
  });

export const useCreateChannelScrapingServer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, scrapingServerId }: { data: ChannelScrapingServerPayload; scrapingServerId?: string | null }) =>
      channelScrapingService.createServer(data, scrapingServerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelScrapingKeys.all }),
  });
};

export const useUpdateChannelScrapingServer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, scrapingServerId }: { id: string; data: ChannelScrapingServerPayload; scrapingServerId?: string | null }) =>
      channelScrapingService.updateServer(id, data, scrapingServerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelScrapingKeys.all }),
  });
};

export const useCreateChannelScrapingWorkerServer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ChannelScrapingWorkerServerPayload) => channelScrapingService.createScrapingServer(request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelScrapingKeys.all }),
  });
};

export const useUpdateChannelScrapingWorkerServer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ChannelScrapingWorkerServerPayload }) =>
      channelScrapingService.updateScrapingServer(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelScrapingKeys.all }),
  });
};

export const useDeleteChannelScrapingWorkerServer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => channelScrapingService.deleteScrapingServer(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelScrapingKeys.all }),
  });
};

export const useCreateChannelScrapingTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ChannelScrapingTaskPayload) => channelScrapingService.createTask(request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelScrapingKeys.all }),
  });
};

export const useDeleteChannelScrapingServer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scrapingServerId }: { id: string; scrapingServerId?: string | null }) => channelScrapingService.deleteServer(id, scrapingServerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelScrapingKeys.all }),
  });
};

export const useUpdateChannelScrapingTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, scrapingServerId }: { id: string; data: ChannelScrapingTaskPayload; scrapingServerId?: string | null }) =>
      channelScrapingService.updateTask(id, data, scrapingServerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelScrapingKeys.all }),
  });
};

export const useDeleteChannelScrapingTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scrapingServerId }: { id: string; scrapingServerId?: string | null }) => channelScrapingService.deleteTask(id, scrapingServerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelScrapingKeys.all }),
  });
};

export const useRunChannelScrapingTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scrapingServerId }: { id: string; scrapingServerId?: string | null }) => channelScrapingService.runTask(id, scrapingServerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelScrapingKeys.all }),
  });
};
